import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LogOut, Search, RefreshCw, Truck, Wrench, AlertTriangle } from "lucide-react";

const STATUSES = ["New", "Received", "Teardown", "Parts Ordered", "Rebuilt", "Ready for Pickup", "Picked Up / Paid"];
const statusClass = (s) => `status-${s.replace(/\s+/g, "").replace("/", "")}`;

export default function AdminBoard() {
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/jobs", { params: q ? { q } : {} });
      setJobs(r.data.jobs);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem("ch_token"); nav("/admin/login"); }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const logout = () => { localStorage.removeItem("ch_token"); nav("/admin/login"); };

  const shown = filter === "all" ? jobs : jobs.filter(j => j.status === filter);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: jobs.filter(j => j.status === s).length }), {});

  return (
    <div className="min-h-screen bg-[#0D0E11] diamond-plate">
      <header className="border-b border-[#2A2F3A] bg-[#0D0E11]/95 sticky top-0 z-30">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 py-4 flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 flex items-center justify-center rounded-sm"><span className="font-display font-black text-white">CH</span></div>
            <div>
              <div className="font-display font-black text-white text-lg uppercase">Shop Board</div>
              <div className="eyebrow">{localStorage.getItem("ch_email")}</div>
            </div>
          </div>
          <div className="flex-1 flex items-center gap-2 max-w-md">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input data-testid="admin-search" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} placeholder="Search name, phone, email, model…" className="input pl-9 py-2" />
            </div>
            <button data-testid="admin-refresh" onClick={load} className="btn-outline p-2 rounded-sm"><RefreshCw className="w-4 h-4" /></button>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="btn-outline px-3 py-2 rounded-sm inline-flex items-center gap-2"><LogOut className="w-4 h-4" /> Logout</button>
        </div>

        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 pb-4 flex flex-wrap gap-2">
          <button onClick={() => setFilter("all")} data-testid="filter-all" className={`pill ${filter === "all" ? "bg-white text-black border-white" : "text-slate-300 border-[#2A2F3A]"}`}>All · {jobs.length}</button>
          {STATUSES.map(s => (
            <button key={s} data-testid={`filter-${s.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setFilter(s)} className={`pill ${statusClass(s)} ${filter === s ? "ring-2 ring-white/40" : ""}`}>{s} · {counts[s]}</button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 sm:px-8 py-6">
        {loading ? (
          <div className="text-slate-400 py-16 text-center">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="text-slate-500 py-24 text-center font-display uppercase text-xl tracking-wider">No jobs on the board.</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {shown.map(j => <JobCard key={j.id} job={j} />)}
          </div>
        )}
      </main>
    </div>
  );
}

function JobCard({ job }) {
  const c = job.customer || {};
  return (
    <Link to={`/admin/jobs/${job.id}`} data-testid={`job-card-${job.id.slice(0,8)}`} className="shop-card p-5 block hover:border-red-500 transition">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-black text-white text-xl uppercase">{c.name}</div>
          <div className="text-slate-400 text-sm">{c.phone} · {c.company || "—"}</div>
        </div>
        <span className={`pill ${statusClass(job.status)}`}>{job.status}</span>
      </div>
      <div className="mt-3 flex items-center gap-2 text-slate-300 text-sm">
        {job.job_type === "onsite" ? <><Truck className="w-4 h-4 text-red-500" /> On-Site</> : <><Wrench className="w-4 h-4 text-red-500" /> Rebuild</>}
        {job.onsite?.is_emergency && <span className="inline-flex items-center gap-1 text-amber-400 font-semibold"><AlertTriangle className="w-3 h-3" /> Emergency</span>}
      </div>
      <div className="mt-3 text-slate-400 text-sm">
        {job.equipment?.make && <div>{job.equipment.make} {job.equipment.model}</div>}
        {job.onsite?.address && <div className="text-xs">{job.onsite.address} · {job.onsite.distance_miles}mi</div>}
      </div>
      <div className="mt-3 pt-3 border-t border-[#2A2F3A] flex items-center justify-between text-xs text-slate-500 font-mono-tech">
        <span>#{job.id.slice(0,8)}</span>
        <span>{job.deposit_status === "paid" ? <span className="text-green-400">$150 PAID</span> : job.deposit_status === "pending" ? <span className="text-amber-400">DEPOSIT PENDING</span> : <span>DROP-OFF</span>}</span>
      </div>
    </Link>
  );
}
